import React, { useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { linearGradientDef } from "@nivo/core";

const StepDataChart = ({ patientData }) => {
  const [chartData, setChartData] = useState([]);
  // const [chartKeys, setChartKeys] = useState([]);
  const [trendLineData, setTrendLineData] = useState([]);
  const [averageSteps, setAverageSteps] = useState(0);
  const redColor = "#ff6384"; // Red color for weekly average

  useEffect(() => {
    if (
      patientData &&
      patientData.month_steps &&
      patientData.month_steps.length >= 2
    ) {
      // Get dates and steps data
      const dates = patientData.month_steps[1] || [];
      const steps = patientData.month_steps[0] || [];

      // Calculate average steps
      if (steps.length > 0) {
        const totalSteps = steps.reduce(
          (sum, current) => sum + (current || 0),
          0
        );
        const avgSteps = Math.round(totalSteps / steps.length);
        setAverageSteps(avgSteps);
      }

      // Create array of data points
      const processedData = dates
        .map((dateStr, index) => {
          const date = new Date(dateStr);
          return {
            date: dateStr,
            formattedDate: date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "2-digit"
            }),
            fullDate: date.toLocaleDateString("en-US", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            steps: steps[index] || 0,
            // Determine color based on step count
            color:
              steps[index] <= 3000
                ? "low"
                : steps[index] <= 7000
                ? "medium"
                : "high",
          };
        })
        .filter((item) => !isNaN(new Date(item.date).getTime())); // Filter out invalid dates

      // Sort data by date
      processedData.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Calculate trend line (simple linear regression)
      if (processedData.length > 1) {
        const xValues = Array.from(Array(processedData.length).keys()); // [0, 1, 2, ...]
        const yValues = processedData.map((d) => d.steps);

        // Calculate trend line using least squares method
        const n = xValues.length;
        const sumX = xValues.reduce((a, b) => a + b, 0);
        const sumY = yValues.reduce((a, b) => a + b, 0);
        const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
        const sumXX = xValues.reduce((sum, x) => sum + x * x, 0);

        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;

        // Generate trend line data points
        const trendLine = xValues.map((x) => ({
          x,
          y: Math.max(0, Math.round(intercept + slope * x)), // Ensure no negative values
        }));

        setTrendLineData(trendLine);
      }

      //console.log(processedData)
      setChartData(processedData);

      const containerWidth = document.getElementById("graph_box").clientWidth;
      document.getElementById("graph_container").scrollLeft = containerWidth;
    }
  }, [patientData]);

  // Custom theme for Nivo
  const theme = {
    background: "transparent",
    text: {
      fontSize: 12,
      fill: "#666666",
      fontFamily:
        "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
    },
    axis: {
      domain: {
        line: {
          stroke: "#dddddd",
          strokeWidth: 1,
        },
      },
      ticks: {
        line: {
          stroke: "#dddddd",
          strokeWidth: 1,
        },
        text: {
          fontSize: 14,
          fill: "#666666",
          fontFamily: "'Poppins', sans-serif",
        },
      },
      legend: {
        text: {
          fontSize: 18,
          fill: "#666666",
          fontFamily: "'Poppins', sans-serif",
        },
      },
    },
    grid: {
      line: {
        stroke: "#eeeeee",
        strokeWidth: 1,
      },
    },
    legends: {
      text: {
        fontSize: 11,
        fill: "#666666",
        fontFamily: "'Poppins', sans-serif",
      },
    },
    tooltip: {
      container: {
        fontSize: 13,
        fontFamily: "'Poppins', sans-serif",
        borderRadius: 3,
        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
      },
    },
    annotations: {
      text: {
        fontSize: 12,
        fill: "#666666",
        fontFamily: "'Poppins', sans-serif",
      },
      link: {
        stroke: "#cccccc",
        strokeWidth: 1,
        outlineWidth: 2,
        outlineColor: "white",
      },
      outline: {
        stroke: "#cccccc",
        strokeWidth: 1,
        outlineWidth: 2,
        outlineColor: "white",
      },
      symbol: {
        fill: "#cccccc",
        outlineWidth: 2,
        outlineColor: "white",
      },
    },
  };

  // Custom tooltip component
  const CustomTooltip = ({ id, value, color, data }) => (
    <div
      style={{
        padding: "10px 12px",
        background: "white",
        border: "1px solid #ccc",
        borderRadius: "3px",
        minWidth: "120px",
      }}
    >
      <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
        {data.fullDate}
      </div>
      <div style={{ color: "#0087ac" }}>
        Steps: <strong>{value.toLocaleString()}</strong>
      </div>
    </div>
  );

  // Create trend line layer
  const TrendLine = ({ bars, xScale, yScale }) => {
    if (!trendLineData.length || !bars.length) return null;

    var xEnd = 0;
    var yEnd = 0;

    // Map trend line points to coordinates
    var points = trendLineData
      .map((point, index) => {
        if (index >= bars.length) return null;

        // Get the center x coordinate of the bar
        const bar = bars[index];
        const x = (bar.x);

        // Get the y coordinate for the trend line point
        const y = yScale(point.y);

        xEnd = bars[bars.length - 1].x + bars[bars.length - 1].width + 10;
        yEnd = bars[bars.length - 1].y - 20

        return `${x},${y}`;
      })
      .filter(Boolean)
      .join(" ");

    //add length to trend line
    const lastpoint = points.substring(points.lastIndexOf(' ') + 1);
    const xLastPoints = lastpoint.substring(0, lastpoint.lastIndexOf(','))
    const yLastPoints = lastpoint.substring(lastpoint.lastIndexOf(',')+1)
    points = points + " " + String(parseInt(xLastPoints)+20) + "," + yLastPoints;

    return (
      <g>
        <polyline
          points={points}
          fill="none"
          stroke="#ff6384" // Red color for trend line
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray="0"
          strokeOpacity={0.8}
        />
        <text
          x={xEnd + 5}
          y={yEnd + 5}
          textAnchor="start"
          fontSize={11}
          fill="#ff6384"
        >
        
        </text>
      </g>
    );
  };

  // Create average line layer
  const AverageLine = ({ bars, xScale, yScale }) => {
    if (!averageSteps || !bars.length) return null;

    // Get y coordinate for average line
    const y = yScale(averageSteps);

    // Get x coordinates for start and end of line
    const xStart = bars[0].x;
    const xEnd = bars[bars.length - 1].x + bars[bars.length - 1].width + 10;

    return (
      <g>
        <line
          x1={xStart}
          x2={xEnd}
          y1={y}
          y2={y}
          stroke="#6b7280" // Gray color for average line
          strokeWidth={2}
          strokeDasharray="5,5"
          strokeOpacity={0.7}
        />
        <text
          x={xEnd + 40}
          y={y + 5}
          textAnchor="start"
          fontSize={18}
          fill="#6b7280"
        >
          &nbsp; Avg: {averageSteps.toLocaleString()}
        </text>
      </g>
    );
  };

  return (
    <div style={{width: "100%"}} className="graph">
      <h3>Step Data Overview</h3>
      <span style={{position: "relative", top: "-30px", fontSize: "18px", lineHeight: "25px"}}> 
        <div>
          <h6 style={{marginLeft: "0px", marginBottom: "10px", color: "#F08A9E" }} >Prediction&ensp; ---- <br></br> </h6>
          <h6 style={{marginLeft: "0px", color: "#989CA5" }} > Average &ensp;  &ensp; ----</h6>
        </div>
      </span>
      {/* Prediction  */}
        <div
          style={{
            marginTop: "-60px"
          }}
        >
        </div>

      <div
        id="graph_container"
        style={{
          width: "100%",
          position: "relative",
          overflowX: "auto", // Allow horizontal scrolling
        }}
      >
        <div
          id="graph_box"
          style={{
            minWidth: Math.max(1000, chartData.length * 25) + "px", // Ensure enough width based on data points
            //width: Math.max(1000, chartData.length * 25) + "px",
            height: "450px",
            minHeight: "400px",
          }}
        >
          <ResponsiveBar
            data={chartData}
            keys={["steps"]}
            indexBy="formattedDate"
            margin={{ top: 60, right: 160, bottom: 70, left: 90 }}
            padding={0.25}
            valueScale={{ type: "linear" }}
            indexScale={{ type: "band"}}
            borderRadius={3}
            colors={({ data }) => {
              // Color based on step count
              if (data.steps <= 3000) return "url(#gradientLow)";
              if (data.steps <= 7000) return "url(#gradientMedium)";
              return "url(#gradientHigh)";
            }}
            borderWidth={1}
            borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
            axisTop={null}
            axisRight={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
            }}
            axisBottom={{
              tickSize: 6,
              tickPadding: 5,
              tickRotation: -45,
              format: (value) => value,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: "Steps",
              legendPosition: "middle",
              legendOffset: -70,
              format: (value) => value.toLocaleString(),
            }}
            enableGridY={true}
            enableGridX={false}
            enableLabel={false}
            theme={theme}
            animate={true}
            motionStiffness={90}
            motionDamping={15}
            tooltip={CustomTooltip}
            layers={[
              "grid",
              "axes",
              "bars",
              "markers",
              "legends",
              "annotations",
              AverageLine, // Add the average line layer
              TrendLine, // Add the trend line layer
            ]}
            defs={[
              linearGradientDef(
                "gradientLow",
                [
                  { offset: 0, color: "rgba(203, 225, 247, 1)" },
                  { offset: 100, color: "rgba(203, 225, 247, 1)" },
                ],
                {
                  gradientTransform: "rotate(90 0.5 0.5)",
                }
              ),
              linearGradientDef(
                "gradientMedium",
                [
                  { offset: 0, color: "rgba(181, 214, 245, 1)" },
                  { offset: 100, color: "rgba(181, 214, 245,1)" },
                ],
                {
                  gradientTransform: "rotate(90 0.5 0.5)",
                }
              ),
              linearGradientDef(
                "gradientHigh",
                [
                  { offset: 0, color: "rgba(153, 200, 247, 0.8)" },
                  { offset: 100, color: "rgba(153, 200, 247, 0.8)" },
                ],
                {
                  gradientTransform: "rotate(90 0.5 0.5)",
                }
              ),
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default StepDataChart;
